import type { LevelConfig, RuleId, RuleKind, Tile } from './types.js';
import type { Rng } from './rng.js';

/** Two tiles pair if this returns true. Never called with a cleared tile. */
export type MatchFn = (a: Tile, b: Tile, cfg: LevelConfig) => boolean;

/** One tile satisfies a hunt rule if this returns true. */
export type TestFn = (value: number, cfg: LevelConfig) => boolean;

/** Produces one matching pair of tile faces for a level. */
export type PairFn = (rng: Rng, cfg: LevelConfig, ctx: DealCtx) => [Face, Face];

export interface Face {
  label: string;
  value: number;
}

/**
 * Per-board memory for the dealer.
 *
 * Without it every pair is drawn independently, which is how a board ends up
 * showing 11 · 22 twice and never showing 4 · 8 at all. The dealer consults
 * this to spread the bases out within a board and across consecutive boards.
 */
export interface DealCtx {
  /** How many times each base has been used on this board. */
  used: Map<number, number>;
  /** Bases dealt on the previous board — deprioritised, not banned. */
  recent: ReadonlySet<number>;
  /** Pairs dealt so far whose base is above `BIG_BASE`. */
  bigUsed: number;
}

export const newDealCtx = (recent: ReadonlySet<number> = new Set()): DealCtx => ({
  used: new Map(),
  recent,
  bigUsed: 0,
});

export interface PairRule {
  id: RuleId;
  kind: 'pair';
  /** Default target, where the rule has one. */
  defaultTarget: number;
  match: MatchFn;
  pair: PairFn;
}

export interface HuntRule {
  id: RuleId;
  kind: 'hunt';
  defaultTarget: number;
  test: TestFn;
  /** Values that belong on neither side of the question and are never dealt. */
  exclude?: (value: number, cfg: LevelConfig) => boolean;
}

export type Rule = PairRule | HuntRule;

const EPS = 1e-9;

/** Smallest and largest number with `d` digits: 1→1..9, 2→10..99, … */
export function digitRange(digits: number): [number, number] {
  const d = Math.max(1, Math.min(4, Math.floor(digits)));
  return [d === 1 ? 1 : 10 ** (d - 1), 10 ** d - 1];
}

/** The level's value window: explicit bounds win over the digit count. */
export function valueRange(cfg: LevelConfig): [number, number] {
  const [dlo, dhi] = digitRange(cfg.digits);
  const lo = cfg.valueMin ?? dlo;
  const hi = cfg.valueMax ?? dhi;
  return lo <= hi ? [lo, hi] : [dlo, dhi];
}

export function isPrime(n: number): boolean {
  if (!Number.isInteger(n) || n < 2) return false;
  if (n % 2 === 0) return n === 2;
  for (let i = 3; i * i <= n; i += 2) if (n % i === 0) return false;
  return true;
}

/* ── pair predicates ─────────────────────────────────────────────────────── */

const equal: MatchFn = (a, b) => a.value === b.value;

const sum: MatchFn = (a, b, cfg) => a.value + b.value === cfg.ruleTarget;

const product: MatchFn = (a, b, cfg) => a.value * b.value === cfg.ruleTarget;

const multiple: MatchFn = (a, b, cfg) => {
  const lo = Math.min(a.value, b.value);
  const hi = Math.max(a.value, b.value);
  if (lo === 0) return false;
  if (!cfg.allowEqualMultiples && lo === hi) return false;
  if (!cfg.allowOneAsFactor && lo === 1) return false;
  if (hi % lo !== 0) return false;
  return !overCap(lo, hi, cfg);
};

/**
 * Is `hi` more than `maxMultiplier` times `lo`? A board "up to ×3" does not
 * accept 3 ↔ 12 even when both happen to be on it — otherwise the cap would
 * only shape the deal, and the class would still be marked right for a fact
 * the lesson has not reached.
 */
export function overCap(lo: number, hi: number, cfg: LevelConfig): boolean {
  return cfg.maxMultiplier > 0 && lo > 0 && hi / lo > cfg.maxMultiplier;
}

const approxEqual: MatchFn = (a, b) => Math.abs(a.value - b.value) < EPS;

const face = (label: string, value: number): Face => ({ label, value });

/* ── the dealer's number spread ──────────────────────────────────────────── */

/** From this base up a number counts as "big" and is rationed. */
const BIG_BASE = 10;
/** At most this many pairs per board may use a big base. */
const BIG_QUOTA = 2;

function weightedPick(rng: Rng, values: readonly number[], weights: readonly number[]): number {
  let total = 0;
  for (const w of weights) total += w;
  if (total <= 0) return rng.pick(values);
  let r = rng.next() * total;
  for (let i = 0; i < values.length; i++) {
    r -= weights[i]!;
    if (r <= 0) return values[i]!;
  }
  return values[values.length - 1]!;
}

/**
 * Picks the base of a multiplication pair.
 *
 * Three pressures, in the order a teacher asked for them. Weight falls as
 * 1/base, so the small numbers that carry the lesson come up most. A board gets
 * at most `BIG_QUOTA` bases of `BIG_BASE` or more, because "once or twice for
 * big numbers is enough". And repeats are discouraged — but only enough to
 * spread the board, not enough to force it upward.
 *
 * That last distinction is the whole trick. A 7×7 board is 24 pairs and there
 * are only eight bases below ten, so a dealer that refused to repeat would have
 * to reach for 11, 13, 17 to fill the board — producing exactly the boards this
 * was meant to eliminate. Repeating 4 · 8 and 4 · 12 is good practice; repeating
 * 11 · 22 is the thing to stop. So a small base is nudged, a big one is rationed.
 */
function pickBase(rng: Rng, ctx: DealCtx, lo: number, hi: number): number {
  const min = Math.max(2, lo);
  const max = Math.max(min, Math.floor(hi / 2));
  const values: number[] = [];
  const weights: number[] = [];
  const bigSpent = ctx.bigUsed >= BIG_QUOTA;

  for (let b = min; b <= max; b++) {
    if (b * 2 > hi) break;
    const big = b >= BIG_BASE;
    if (big && bigSpent) continue;
    const seen = ctx.used.get(b) ?? 0;
    let w = 1 / b;
    // Strong enough that every small base gets a turn before any comes round
    // again, weak enough that coming round again beats reaching for 17.
    w *= big ? (seen ? 0.12 / seen : 1) : 1 / (1 + seen * 8);
    if (ctx.recent.has(b)) w *= 0.45;
    values.push(b);
    weights.push(w);
  }
  // Every base is spoken for and the big ones are spent: reopen the small ones.
  if (values.length === 0) {
    for (let b = min; b <= max && b * 2 <= hi; b++) { values.push(b); weights.push(1 / b); }
  }
  if (values.length === 0) return min;
  return weightedPick(rng, values, weights);
}

/**
 * Multipliers are pulled toward 2–5 for the same reason bases are pulled small,
 * and never pass the teacher's cap.
 */
function pickMultiplier(rng: Rng, base: number, hi: number, cap: number): number {
  let maxK = Math.max(2, Math.floor(hi / base));
  if (cap > 0) maxK = Math.max(2, Math.min(maxK, cap));
  const values: number[] = [];
  const weights: number[] = [];
  for (let k = 2; k <= maxK; k++) {
    values.push(k);
    weights.push(k <= 5 ? 1 : 0.35 / (k - 4));
  }
  return values.length ? weightedPick(rng, values, weights) : 2;
}

function noteBase(ctx: DealCtx, base: number): void {
  ctx.used.set(base, (ctx.used.get(base) ?? 0) + 1);
  if (base >= BIG_BASE) ctx.bigUsed += 1;
}

/* ── pair dealers ────────────────────────────────────────────────────────── */

/**
 * `sumTo` needs both halves to exist in the level's digit range, so the first
 * half is drawn from the window that leaves a valid partner.
 */
function sumPair(rng: Rng, cfg: LevelConfig): [Face, Face] {
  const [lo, hi] = valueRange(cfg);
  const t = cfg.ruleTarget;
  const min = Math.max(lo, t - hi);
  const max = Math.min(hi, t - lo);
  if (min > max) {
    // The window cannot reach the target (e.g. 3-digit tiles summing to 10).
    // This used to deal an *equal* pair — two tiles that can never sum to the
    // target, stranded on the board. A real pair that leaves the window is the
    // lesser evil; the teacher panel warns about the range.
    const a = Math.max(0, Math.min(t, rng.int(lo, hi)));
    return [face(String(a), a), face(String(t - a), t - a)];
  }
  const a = rng.int(min, max);
  return [face(String(a), a), face(String(t - a), t - a)];
}

function equalPair(rng: Rng, cfg: LevelConfig): [Face, Face] {
  const [lo, hi] = valueRange(cfg);
  const v = rng.int(lo, hi);
  return [face(String(v), v), face(String(v), v)];
}

function multiplePair(rng: Rng, cfg: LevelConfig, ctx: DealCtx): [Face, Face] {
  const [lo, hi] = valueRange(cfg);
  const base = pickBase(rng, ctx, cfg.allowOneAsFactor ? Math.max(1, lo) : Math.max(2, lo), hi);
  if (base * 2 > hi && cfg.allowEqualMultiples) {
    // The window is too narrow for any ×2 (say 6–9). An equal pair is allowed
    // here and keeps every tile inside the range the teacher set.
    const v = rng.int(Math.max(base, lo), hi);
    noteBase(ctx, v);
    return [face(String(v), v), face(String(v), v)];
  }
  // Otherwise ×2 of the smallest base is the honest fallback: it may sit above
  // the window, but it is a real pair rather than a tile with no partner.
  const other = base * pickMultiplier(rng, base, hi, cfg.maxMultiplier);
  noteBase(ctx, base);
  return [face(String(base), base), face(String(other), other)];
}

/**
 * `productTo`: two numbers whose product is the target — 3 ↔ 4 for twelve.
 *
 * Note the weighting runs the *opposite* way to `multipleOf`. There, small is
 * the lesson. Here, small means trivial: 1 × 12 teaches nothing a child cannot
 * already see, and since 1 has the largest 1/a weight, a small-first dealer
 * fills the board with 1s and 12s. So weight rises toward the middle of the
 * factor list, where 3 × 4 and 2 × 6 live, and 1 is pushed to the edges.
 */
function productPairTo(rng: Rng, cfg: LevelConfig, ctx: DealCtx): [Face, Face] {
  const t = Math.max(1, Math.floor(cfg.ruleTarget));
  const [lo, hi] = valueRange(cfg);
  const factors: number[] = [];
  const weights: number[] = [];
  for (let a = 1; a * a <= t; a++) {
    if (t % a !== 0) continue;
    const b = t / a;
    if (a < lo || a > hi || b < lo || b > hi) continue;
    if (!cfg.allowOneAsFactor && a === 1) continue;
    if (!cfg.allowEqualMultiples && a === b) continue;
    const seen = ctx.used.get(a) ?? 0;
    // Toward the square root, and away from the trivial 1 × target.
    let w = (a === 1 ? 0.22 : a) / (1 + seen * 8);
    if (ctx.recent.has(a)) w *= 0.45;
    factors.push(a);
    weights.push(w);
  }
  if (factors.length === 0) {
    // No factor pair fits the window — 1 × t is always true, so it is the
    // honest fallback rather than dealing a tile with no partner.
    return [face('1', 1), face(String(t), t)];
  }
  const a = weightedPick(rng, factors, weights);
  noteBase(ctx, a);
  return [face(String(a), a), face(String(t / a), t / a)];
}

/**
 * The factors a `sameProduct` board may use.
 *
 * With no explicit range it keeps the built-in spread — 2–9 for the first
 * expression, up to 12 for its partner. With one, every factor on every tile
 * stays inside it (floored at 2, since × 1 makes every pair trivial, and
 * capped at 20 so a wide range does not produce 17 × 19).
 */
function productFactors(cfg: LevelConfig): { lo: number; hi: number; altHi: number } {
  if (cfg.valueMin === undefined && cfg.valueMax === undefined) return { lo: 2, hi: 9, altHi: 12 };
  const [vlo, vhi] = valueRange(cfg);
  const lo = Math.max(2, Math.min(vlo, 20));
  const hi = Math.max(lo, Math.min(vhi, 20));
  return { lo, hi, altHi: hi };
}

/** G5 shows expressions; the value is the product, so the predicate is equality. */
function sameProductPair(rng: Rng, cfg: LevelConfig, ctx: DealCtx): [Face, Face] {
  const f = productFactors(cfg);
  for (let attempt = 0; attempt < 24; attempt++) {
    // Small products first, for the same reason small bases come first.
    const a = pickSmall(rng, f.lo, f.hi, ctx);
    const b = rng.int(f.lo, f.hi);
    const p = a * b;
    const alts: Array<[number, number]> = [];
    for (let x = f.lo; x <= f.altHi; x++) {
      if (p % x === 0) {
        const y = p / x;
        if (y >= f.lo && y <= f.altHi && !(x === a && y === b)) alts.push([x, y]);
      }
    }
    if (alts.length) {
      const [x, y] = rng.pick(alts);
      noteBase(ctx, a);
      return [face(`${a}×${b}`, p), face(`${x}×${y}`, p)];
    }
  }
  // a × b ↔ b × a always exists, and stays inside the window.
  const v = rng.int(f.lo, f.hi);
  const w = rng.int(f.lo, f.hi);
  return [face(`${v}×${w}`, v * w), face(`${w}×${v}`, v * w)];
}

/** A small-first draw that also avoids repeating within the board. */
function pickSmall(rng: Rng, lo: number, hi: number, ctx: DealCtx): number {
  const values: number[] = [];
  const weights: number[] = [];
  for (let v = lo; v <= hi; v++) {
    const seen = ctx.used.get(v) ?? 0;
    const w = (1 / v) / (1 + seen * 8);
    values.push(v);
    weights.push(w);
  }
  return weightedPick(rng, values, weights);
}

/** G6 mixes representations of the same quantity: 1/2, 2/4, 0.5, 50%. */
const EQUIV: ReadonlyArray<readonly string[]> = [
  ['1/2', '2/4', '0.5', '50%'],
  ['1/4', '2/8', '0.25', '25%'],
  ['3/4', '6/8', '0.75', '75%'],
  ['1/5', '2/10', '0.2', '20%'],
  ['1/10', '2/20', '0.1', '10%'],
  ['1/3', '2/6', '3/9', '33%'],
  ['2/3', '4/6', '6/9', '67%'],
  ['1/1', '2/2', '1.0', '100%'],
  ['3/5', '6/10', '0.6', '60%'],
  ['2/5', '4/10', '0.4', '40%'],
];

/** Parses "3/4", "0.75" or "75%" to a number. Rounded so 1/3 and 33% agree. */
export function parseFaceValue(label: string): number {
  let v: number;
  if (label.endsWith('%')) v = parseFloat(label) / 100;
  else if (label.includes('/')) {
    const [n, d] = label.split('/');
    v = Number(n) / Number(d);
  } else v = Number(label);
  return Math.round(v * 100) / 100;
}

function equivalentPair(rng: Rng): [Face, Face] {
  const group = rng.pick(EQUIV);
  const i = rng.int(0, group.length - 1);
  let j = rng.int(0, group.length - 1);
  if (j === i) j = (j + 1) % group.length;
  const la = group[i]!;
  const lb = group[j]!;
  return [face(la, parseFaceValue(la)), face(lb, parseFaceValue(lb))];
}

/* ── the catalogue ───────────────────────────────────────────────────────── */

const belowTwo = (v: number): boolean => v < 2;

export const RULES: Record<RuleId, Rule> = {
  equal:       { id: 'equal',       kind: 'pair', defaultTarget: 0,  match: equal,       pair: equalPair },
  sumTo:       { id: 'sumTo',       kind: 'pair', defaultTarget: 10, match: sum,         pair: sumPair },
  sumTo20:     { id: 'sumTo20',     kind: 'pair', defaultTarget: 20, match: sum,         pair: sumPair },
  multipleOf:  { id: 'multipleOf',  kind: 'pair', defaultTarget: 0,  match: multiple,    pair: multiplePair },
  productTo:   { id: 'productTo',   kind: 'pair', defaultTarget: 12, match: product,     pair: productPairTo },
  sameProduct: { id: 'sameProduct', kind: 'pair', defaultTarget: 0,  match: equal,       pair: sameProductPair },
  equivalent:  { id: 'equivalent',  kind: 'pair', defaultTarget: 0,  match: approxEqual, pair: equivalentPair },

  even:        { id: 'even',        kind: 'hunt', defaultTarget: 0,  test: (v) => v % 2 === 0 },
  odd:         { id: 'odd',         kind: 'hunt', defaultTarget: 0,  test: (v) => Math.abs(v % 2) === 1 },
  // 1 is neither prime nor composite. Rather than force a six-year-old to
  // adjudicate that, it is never dealt on either board.
  prime:       { id: 'prime',       kind: 'hunt', defaultTarget: 0,  test: (v) => isPrime(v), exclude: belowTwo },
  notPrime:    { id: 'notPrime',    kind: 'hunt', defaultTarget: 0,  test: (v) => v >= 2 && !isPrime(v), exclude: belowTwo },
  // With a cap the targets are N, 2N, … N×k. A multiple beyond that is left
  // off the board entirely: shown as a decoy it would be a trap — it IS a
  // multiple — and shown as a target it would be outside the lesson.
  multiplesOf: {
    id: 'multiplesOf', kind: 'hunt', defaultTarget: 2,
    test: (v, c) => c.ruleTarget > 0 && v > 0 && v % c.ruleTarget === 0,
    exclude: (v, c) => c.ruleTarget > 0 && v % c.ruleTarget === 0 &&
      (v <= 0 || (c.maxMultiplier > 0 && v > c.ruleTarget * c.maxMultiplier)),
  },
  factorsOf:   { id: 'factorsOf',   kind: 'hunt', defaultTarget: 12, test: (v, c) => v > 0 && c.ruleTarget % v === 0 },
};

export const ruleKind = (id: RuleId): RuleKind => RULES[id].kind;

export const isHunt = (cfg: LevelConfig): boolean => RULES[cfg.rule].kind === 'hunt';

/** Rules the multiplier cap applies to. */
export const CAPPED_RULES: ReadonlySet<RuleId> = new Set<RuleId>(['multipleOf', 'multiplesOf']);

/** Rules whose tiles are not plain numbers, so a number range means nothing. */
export const RANGELESS_RULES: ReadonlySet<RuleId> = new Set<RuleId>(['equivalent']);

/** Rules whose `ruleTarget` is a real setting the teacher should see. */
export const TARGETED_RULES: ReadonlySet<RuleId> =
  new Set<RuleId>(['sumTo', 'sumTo20', 'productTo', 'multiplesOf', 'factorsOf']);

/** Do these two tile faces satisfy the level's rule? */
export function isMatch(a: Tile, b: Tile, cfg: LevelConfig): boolean {
  if (a.id === b.id || a.cleared || b.cleared) return false;
  const rule = RULES[cfg.rule];
  return rule.kind === 'pair' ? rule.match(a, b, cfg) : false;
}

/** Does this single tile satisfy the level's hunt rule? */
export function satisfies(tile: Tile, cfg: LevelConfig): boolean {
  const rule = RULES[cfg.rule];
  return rule.kind === 'hunt' ? rule.test(tile.value, cfg) : false;
}
